import { useState } from "react";
import { DashboardLayout } from "@/components/Dashboard/DashboardLayout";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/apiClient";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Users, Plus, Edit, Search, Building2, User, MapPin, Phone, Mail,
  CreditCard, AlertTriangle, FileText, ArrowLeft
} from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import type { BillingCustomer, TaxRate } from "@/types/billing";

const emptyCustomer: Partial<BillingCustomer> = {
  customer_type: 'company',
  tax_id_type: 'NIF',
  legal_name: '',
  country_code: 'ES',
  default_payment_terms_days: 30,
  credit_limit: 0,
  is_active: true,
};

export default function BillingCustomers() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState<Partial<BillingCustomer> | null>(null);

  // Fetch customers
  const { data: customers = [], isLoading } = useQuery<BillingCustomer[]>({
    queryKey: ["billing-customers", search],
    queryFn: async () => {
      let query = api
        .from("billing_customers")
        .select("*")
        .order("legal_name");

      if (search) {
        query = query.or(`legal_name.ilike.%${search}%,tax_id.ilike.%${search}%,trade_name.ilike.%${search}%`);
      }

      const { data, error } = await query;
      if (error) throw error;
      return data || [];
    },
    enabled: !!user,
  });

  // Fetch tax rates for default
  const { data: taxRates = [] } = useQuery<TaxRate[]>({
    queryKey: ["tax-rates"],
    queryFn: async () => {
      const { data, error } = await api
        .from("tax_rates")
        .select("*")
        .eq("is_active", true)
        .order("rate", { ascending: false });
      if (error) throw error;
      return data || [];
    },
  });

  // Save mutation
  const saveMutation = useMutation({
    mutationFn: async (customer: Partial<BillingCustomer>) => {
      if (customer.id) {
        const { error } = await api
          .from("billing_customers")
          .update({
            ...customer,
            updated_at: new Date().toISOString(),
            updated_by: user?.id,
          })
          .eq("id", customer.id);
        if (error) throw error;
      } else {
        const { error } = await api
          .from("billing_customers")
          .insert({
            ...customer,
            created_by: user?.id,
          });
        if (error) throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["billing-customers"] });
      setDialogOpen(false);
      setEditingCustomer(null);
      toast.success(editingCustomer?.id ? "Cliente actualizado" : "Cliente creado");
    },
    onError: (error: any) => {
      toast.error(`Error: ${error.message}`);
    },
  });

  const handleNew = () => {
    setEditingCustomer({ ...emptyCustomer });
    setDialogOpen(true);
  };

  const handleEdit = (customer: BillingCustomer) => {
    setEditingCustomer(customer);
    setDialogOpen(true);
  };

  const handleSave = () => {
    if (!editingCustomer?.legal_name) {
      toast.error("El nombre es obligatorio");
      return;
    }
    saveMutation.mutate(editingCustomer);
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR' }).format(amount);
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" asChild>
              <Link to="/billing"><ArrowLeft className="w-4 h-4" /></Link>
            </Button>
            <div>
              <h1 className="text-3xl font-bold">Clientes</h1>
              <p className="text-muted-foreground">Gestión de clientes para facturación</p>
            </div>
          </div>
          <Button onClick={handleNew}>
            <Plus className="w-4 h-4 mr-2" />
            Nuevo Cliente
          </Button>
        </div>

        {/* Search */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Buscar por nombre, NIF o nombre comercial..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Customers Table */}
        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Cliente</TableHead>
                  <TableHead>NIF/CIF</TableHead>
                  <TableHead>Contacto</TableHead>
                  <TableHead>Saldo</TableHead>
                  <TableHead>Estado</TableHead>
                  <TableHead className="text-right">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-8">Cargando...</TableCell>
                  </TableRow>
                ) : customers.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                      {search ? "No se encontraron clientes" : "No hay clientes. Crea el primero."}
                    </TableCell>
                  </TableRow>
                ) : (
                  customers.map((customer) => (
                    <TableRow key={customer.id}>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                            customer.customer_type === 'company' ? 'bg-blue-100' : 'bg-green-100'
                          }`}>
                            {customer.customer_type === 'company' ? (
                              <Building2 className="w-5 h-5 text-blue-600" />
                            ) : (
                              <User className="w-5 h-5 text-green-600" />
                            )}
                          </div>
                          <div>
                            <p className="font-medium">{customer.legal_name}</p>
                            {customer.trade_name && (
                              <p className="text-xs text-muted-foreground">{customer.trade_name}</p>
                            )}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="font-mono">{customer.tax_id || '-'}</TableCell>
                      <TableCell>
                        <div className="text-sm space-y-1">
                          {customer.email && (
                            <div className="flex items-center gap-1 text-muted-foreground">
                              <Mail className="w-3 h-3" />
                              {customer.email}
                            </div>
                          )}
                          {customer.phone && (
                            <div className="flex items-center gap-1 text-muted-foreground">
                              <Phone className="w-3 h-3" />
                              {customer.phone}
                            </div>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className={customer.current_balance > 0 ? 'text-amber-600 font-medium' : ''}>
                          {formatCurrency(customer.current_balance)}
                        </span>
                      </TableCell>
                      <TableCell>
                        {customer.is_blocked ? (
                          <Badge variant="destructive">Bloqueado</Badge>
                        ) : customer.is_active ? (
                          <Badge className="bg-green-100 text-green-700">Activo</Badge>
                        ) : (
                          <Badge variant="secondary">Inactivo</Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button variant="ghost" size="sm" onClick={() => handleEdit(customer)}>
                          <Edit className="w-4 h-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>

      {/* Customer Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingCustomer?.id ? "Editar Cliente" : "Nuevo Cliente"}
            </DialogTitle>
          </DialogHeader>

          {editingCustomer && (
            <Tabs defaultValue="general" className="w-full">
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="general">General</TabsTrigger>
                <TabsTrigger value="contact">Contacto</TabsTrigger>
                <TabsTrigger value="billing">Facturación</TabsTrigger>
              </TabsList>

              <TabsContent value="general" className="space-y-4 mt-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Tipo de cliente</Label>
                    <Select
                      value={editingCustomer.customer_type}
                      onValueChange={(v) => setEditingCustomer({ ...editingCustomer, customer_type: v as any })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="company">Empresa</SelectItem>
                        <SelectItem value="individual">Particular</SelectItem>
                        <SelectItem value="public_admin">Administración Pública</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Código cliente</Label>
                    <Input
                      value={editingCustomer.customer_code || ''}
                      onChange={(e) => setEditingCustomer({ ...editingCustomer, customer_code: e.target.value })}
                      placeholder="Auto si vacío"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>Tipo documento</Label>
                    <Select
                      value={editingCustomer.tax_id_type}
                      onValueChange={(v) => setEditingCustomer({ ...editingCustomer, tax_id_type: v as any })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="NIF">NIF</SelectItem>
                        <SelectItem value="CIF">CIF</SelectItem>
                        <SelectItem value="NIE">NIE</SelectItem>
                        <SelectItem value="PASSPORT">Pasaporte</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="col-span-2 space-y-2">
                    <Label>NIF/CIF *</Label>
                    <Input
                      value={editingCustomer.tax_id || ''}
                      onChange={(e) => setEditingCustomer({ ...editingCustomer, tax_id: e.target.value.toUpperCase() })}
                      placeholder="B12345678"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Razón Social *</Label>
                  <Input
                    value={editingCustomer.legal_name || ''}
                    onChange={(e) => setEditingCustomer({ ...editingCustomer, legal_name: e.target.value })}
                    placeholder="Nombre legal de la empresa"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Nombre Comercial</Label>
                  <Input
                    value={editingCustomer.trade_name || ''}
                    onChange={(e) => setEditingCustomer({ ...editingCustomer, trade_name: e.target.value })}
                    placeholder="Nombre comercial (opcional)"
                  />
                </div>
              </TabsContent>

              <TabsContent value="contact" className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label>Persona de contacto</Label>
                  <Input
                    value={editingCustomer.contact_name || ''}
                    onChange={(e) => setEditingCustomer({ ...editingCustomer, contact_name: e.target.value })}
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Email</Label>
                    <Input
                      type="email"
                      value={editingCustomer.email || ''}
                      onChange={(e) => setEditingCustomer({ ...editingCustomer, email: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Teléfono</Label>
                    <Input
                      value={editingCustomer.phone || ''}
                      onChange={(e) => setEditingCustomer({ ...editingCustomer, phone: e.target.value })}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Dirección</Label>
                  <Input
                    value={editingCustomer.address_line1 || ''}
                    onChange={(e) => setEditingCustomer({ ...editingCustomer, address_line1: e.target.value })}
                    placeholder="Calle, número, piso..."
                  />
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>Código Postal</Label>
                    <Input
                      value={editingCustomer.postal_code || ''}
                      onChange={(e) => setEditingCustomer({ ...editingCustomer, postal_code: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Ciudad</Label>
                    <Input
                      value={editingCustomer.city || ''}
                      onChange={(e) => setEditingCustomer({ ...editingCustomer, city: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Provincia</Label>
                    <Input
                      value={editingCustomer.province || ''}
                      onChange={(e) => setEditingCustomer({ ...editingCustomer, province: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Pais</Label>
                    <Input
                      value={editingCustomer.country_code || 'ES'}
                      onChange={(e) => setEditingCustomer({ ...editingCustomer, country_code: e.target.value })}
                      placeholder="ES"
                    />
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="billing" className="space-y-4 mt-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Forma de pago</Label>
                    <Select
                      value={editingCustomer.default_payment_method || 'transfer'}
                      onValueChange={(v) => setEditingCustomer({ ...editingCustomer, default_payment_method: v })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="transfer">Transferencia</SelectItem>
                        <SelectItem value="cash">Efectivo</SelectItem>
                        <SelectItem value="card">Tarjeta</SelectItem>
                        <SelectItem value="direct_debit">Domiciliación</SelectItem>
                        <SelectItem value="check">Cheque</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Días de pago</Label>
                    <Input
                      type="number"
                      value={editingCustomer.default_payment_terms_days || 30}
                      onChange={(e) => setEditingCustomer({ ...editingCustomer, default_payment_terms_days: parseInt(e.target.value) || 30 })}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>IVA por defecto</Label>
                  <Select
                    value={editingCustomer.default_tax_rate_id || '__none__'}
                    onValueChange={(v) => setEditingCustomer({ ...editingCustomer, default_tax_rate_id: v === '__none__' ? '' : v })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Usar IVA general" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">Usar IVA general</SelectItem>
                      {taxRates.map((rate) => (
                        <SelectItem key={rate.id} value={rate.id}>
                          {rate.name} ({rate.rate}%)
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>IBAN</Label>
                  <Input
                    value={editingCustomer.bank_account_iban || ''}
                    onChange={(e) => setEditingCustomer({ ...editingCustomer, bank_account_iban: e.target.value.toUpperCase().replace(/\s/g, '') })}
                    placeholder="ES00 0000 0000 0000 0000 0000"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Límite de crédito</Label>
                    <Input
                      type="number"
                      value={editingCustomer.credit_limit || 0}
                      onChange={(e) => setEditingCustomer({ ...editingCustomer, credit_limit: parseFloat(e.target.value) || 0 })}
                    />
                  </div>
                  <div className="space-y-2 flex items-end">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={editingCustomer.is_active !== false}
                        onChange={(e) => setEditingCustomer({ ...editingCustomer, is_active: e.target.checked })}
                        className="rounded"
                      />
                      <span>Cliente activo</span>
                    </label>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Notas internas</Label>
                  <Textarea
                    value={editingCustomer.notes || ''}
                    onChange={(e) => setEditingCustomer({ ...editingCustomer, notes: e.target.value })}
                    rows={3}
                  />
                </div>
              </TabsContent>
            </Tabs>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={handleSave} disabled={saveMutation.isPending}>
              {saveMutation.isPending ? "Guardando..." : "Guardar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
